import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { STATUS } from "../../design/ui";
import { normalizeProject, statusKey, toPlainText } from "../../design/domain";
import { hasPermission } from "../../utils/permission";
import { useApp } from "../../design/AppContext";
import { GOAL_DONE_RULE, buildScheduleView, ymdDiff } from "./rdSummary";
import useGoalBufferDays from "./useGoalBufferDays";
import GoalTimeline from "./GoalTimeline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, DataRow, EmptyState, RowButton, StatusChip, TASK_STATUS_KIND } from "../../components/mui";
import { SkeletonRows } from "../../components/loading";
import {
  MdAdd,
  MdCheck,
  MdCircle,
  MdErrorOutline,
  MdPersonOutline,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";

/**
 * 時程 — 里程碑（設計稿 #screen-schedule，el-pm-template_4.html L1402-1707）
 *
 * 這一頁取代舊的「時程日曆」當成時程的入口。主角不是日期格子，是 PM／高階主管
 * 訂下的「里程碑」：每個里程碑先有一張說得清楚的卡（哪一天要交、要達成什麼、
 * 還剩幾天），再有一條軸。軸只是輔助 —— 舊系統只畫線不寫字，看得到有東西在跑，
 * 但看不出來哪一條會爆掉。
 *
 * 同一份資料要畫兩次，缺一不可：
 *   · 橫向時程軸（TRACK_SX），給桌機標準字級看長短與重疊
 *   · 直向階段清單（STAGES_SX），給手機（≤900px）與大字模式看
 * 兩者用 [data-textsize="lg"] 與 @media 自動二選一顯示，
 * 所以兩個都必須在 DOM 裡。只做一個的話，大字模式或手機上整頁會是空白。
 *
 * 版面：外層不再包一層頁面容器。Layout.js 的內容區已經處理內距，
 * 而 /Schedule 命中 Layout 的 NO_PAGE_CAP，所以這一頁
 * 只拿到內距、不吃 1120px 上限（橫向軸需要寬度）。這裡不自己寫 max-width。
 *
 * 資料來源與還剩下的誠實缺口，見檔尾 mapStateToProps 上方的說明。
 */

/* ===================== 常數 ===================== */

/** 沒排進里程碑的工項一次最多列幾筆；其餘靠底下那顆按鈕帶去排工項頁 */
const UNPLANNED_MAX = 8;

/* 橫向時程軸（GoalTimeline）的外框：大字模式與窄螢幕改看直向階段清單 */
const TRACK_SX = {
  '[data-textsize="lg"] &': { display: "none" },
  "@media (max-width: 900px)": { display: "none" },
};

/** 直向階段清單：預設不顯示，大字模式與窄螢幕才顯示（與 TRACK_SX 二選一） */
const STAGES_SX = {
  display: "none",
  '[data-textsize="lg"] &': { display: "block" },
  "@media (max-width: 900px)": { display: "block" },
};

/** 等級 → StatusChip 的 kind（符號照設計稿：紅 ⚠、黃 ！、藍 ●、綠 ✓、灰 ―） */
const LEVEL_TAG = {
  attention: "attention",
  warn: "warn",
  done: "done",
  closed: "closed",
  plan: "progress",
};

/**
 * 等級 → 主題顏色。里程碑列左緣那條色條用的就是它。
 * 值與 LEGEND 的色塊一致（都是長條本身的顏色），所以圖例講的顏色跟列上的一樣，
 * 而且深淺色主題會一起換。plan 不給顏色 —— 「照計畫走」不需要在清單上被標出來。
 */
const LEVEL_COLOR = {
  attention: "error.main",
  warn: "warning.main",
  done: "success.main",
  closed: "grey.500",
};

/** 狀態 → StatusChip 的前綴符號（與 pages/MyTask/Index.js 的 STATUS_MARK 同一份規則）；kind 用共用的 TASK_STATUS_KIND */
const STATUS_MARK = {
  pending: <MdRemove />,
  progress: <MdCircle size="0.6em" />,
  review: <MdErrorOutline />,
  done: <MdCheck />,
  closed: <MdRemove />,
};

/**
 * 軸下的顏色圖例。
 *
 * 四種顏色沒有圖例就沒有人看得懂，而且色盲的人只靠顏色分不出紅黃。
 * 色塊用的是長條本身的顏色（BAR_SX / BAR_LEVEL_SX），不是另外挑的顏色，
 * 所以深淺色主題會一起換。
 */
const LEGEND = [
  { color: "error.main", text: "已超過里程碑日期或逾期" },
  { color: "warning.main", text: "緩衝不足" },
  { color: "primary.main", text: "進度正常" },
  { color: "success.main", text: "已完成" },
  { color: "grey.500", text: "未指派" },
];

/* ===================== 取值與格式 ===================== */

/**
 * 後端的 DateTimeOffset → 當地時區的 "YYYY-MM-DD"。
 *
 * 所有日期算術都在 rdSummary.js 用這個字串做，理由是後端的 end_date / due_date
 * 是帶時區的時刻，直接比 UTC 的話台北時間早上八點以前的那一天會整段位移。
 * 「哪一天」只能在這裡用 dayjs 照當地日界切一次，切完就不要再回到時刻。
 */
function ymdOf(v) {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

/** "2026-10-15" → 「10 月 15 日」；跨年才補年份（與 Today 的 dueLabel 同一種寫法） */
function longDate(ymd) {
  if (!ymd) return "";
  const d = dayjs(ymd);
  if (!d.isValid()) return "";
  return d.isSame(dayjs(), "year") ? d.format("M 月 D 日") : d.format("YYYY 年 M 月 D 日");
}

/** "2026-10-15" → "10/15"（清單列與刻度用的短日期） */
function shortDate(ymd) {
  if (!ymd) return "";
  const d = dayjs(ymd);
  return d.isValid() ? d.format("M/D") : "";
}

/**
 * 人名那一格要寫什麼。
 *
 * /Phase/List 回的是一組（帳號, 姓名）：姓名是後端拿帳號去 Admin 表換來的，
 * 帳號被刪掉或改過的時候換不回來，這時候回的是空字串。
 * 換不到姓名就退回帳號 —— 帳號是那筆資料上真的存在的值，寫出來看的人還查得到
 * 是誰；兩個都沒有才回空字串，由呼叫端整行不渲染（硬規則 2）。
 * 任何情況都不編一個名字上去。
 */
function personLabel(name, account) {
  const n = String(name || "").trim();
  return n || String(account || "").trim();
}

/** 清單列第二格的日期。沒排時間就明講，不要留白讓人以為是資料沒載到 */
function rangeText(t, todayYmd) {
  if (!t.startYmd && !t.dueYmd) return "尚未排定時間";
  if (t.dueYmd && ymdDiff(todayYmd, t.dueYmd) === 0) return `今日 ${shortDate(t.dueYmd)} 到期`;
  if (t.startYmd && t.dueYmd && t.startYmd !== t.dueYmd) {
    return `${shortDate(t.startYmd)} – ${shortDate(t.dueYmd)}`;
  }
  return t.dueYmd ? `${shortDate(t.dueYmd)} 到期` : `${shortDate(t.startYmd)} 開始`;
}

/**
 * 清單列右側的徽章。規則與 pages/MyTask/Index.js 的 tagOf 一致：
 * 期限（今天要交／已逾期）一律搶下 attention，其餘才顯示狀態。
 *
 * 逾期用日界比較（ymdDiff < 0），不是「截止時刻 < 現在」——
 * due_date 存的是當天 00:00，用時刻比的話今天到期的工項早上八點就被判成逾期。
 */
function tagOf(t, todayYmd) {
  const k = t.statusKey;
  if (k !== "done" && k !== "closed") {
    const d = t.dueYmd ? ymdDiff(todayYmd, t.dueYmd) : null;
    if (d === 0) return { kind: "attention", mark: <MdWarningAmber />, text: "今日到期" };
    if (d !== null && d < 0) return { kind: "attention", mark: <MdWarningAmber />, text: "已逾期" };
  }
  const key = STATUS_MARK[k] ? k : "pending";
  return { kind: TASK_STATUS_KIND[key], mark: STATUS_MARK[key], text: (STATUS[k] || STATUS.pending).label };
}

/**
 * 里程碑卡與階段標頭的徽章：把「還剩幾天」跟「這樣排來不來得及」寫成同一句。
 * 設計稿 L1449「還有 46 天 · 照計畫」、L1435「還有 13 天 · 只剩 2 天緩衝」。
 */
function goalTag(g) {
  /* 里程碑的狀態完全由底下的工項推導：工項全部完成＝達成 */
  if (g.level === "done") return { kind: LEVEL_TAG.done, mark: <MdCheck />, text: "已完成 · 工項全數完成" };

  const d = g.daysLeft;

  /* 沒有工項的里程碑：判定不了完成、也判定不了逾期，只講事實（見 rdSummary 的 empty）。
     放在「未設定日期」之前：沒有工項時，「未開始」比「沒訂日期」更是重點。灰色、不進風險橫幅。 */
  if (g.level === "empty") {
    return {
      kind: LEVEL_TAG.closed,
      mark: <MdRemove />,
      text: d !== null && d < 0 ? `已過里程碑日期 ${-d} 天 · 尚無工項` : "未開始 · 尚無工項",
    };
  }

  /* 沒訂 end_date 的里程碑算不出任何天數。寫「還有 0 天」會是錯的，直接說沒訂 */
  if (!g.endYmd) return { kind: LEVEL_TAG.closed, mark: <MdRemove />, text: "未設定里程碑日期" };

  const left = d > 0 ? `剩餘 ${d} 天` : d === 0 ? "今日到期" : `已逾期 ${-d} 天`;

  if (g.level === "attention") {
    return {
      kind: LEVEL_TAG.attention,
      mark: <MdWarningAmber />,
      text: g.overrunDays > 0 ? `${left} · 超出 ${g.overrunDays} 天` : `${left} · 尚未完成`,
    };
  }
  if (g.level === "warn") {
    return { kind: LEVEL_TAG.warn, mark: <MdErrorOutline />, text: `${left} · 緩衝剩 ${g.bufferLeft} 天` };
  }
  return { kind: LEVEL_TAG.plan, mark: <MdCircle size="0.6em" />, text: `${left} · 進度正常` };
}

/**
 * 里程碑卡的件數那一行：件數之後只講一件最該知道的事。
 * 全部講完會變成一長串，而一長串等於什麼都沒講。
 */
function countsLine(g) {
  if (g.counts.total === 0) return "尚無排入的工項";
  const c = g.counts;
  const tail = c.overdue
    ? `${c.overdue} 件已逾期`
    : c.overrun
    ? `${c.overrun} 件超出里程碑日期`
    : c.dueToday
    ? `${c.dueToday} 件今日到期`
    : c.undated
    ? `${c.undated} 件尚未排定時間`
    : c.unassigned
    ? `${c.unassigned} 件未指派`
    : `${c.done} 件已完成`;
  return `${c.total} 件工項 · ${tail}`;
}

/* ===================== 共用零件 ===================== */

/**
 * 全系統通用的清單列。欄位順序與徽章規則照 pages/MyTask/Index.js 的 ElRow：
 * 標題在上，負責／日期在下，右側是徽章與 ›。整列是一顆按鈕，
 * 可點區域遠大於 44px 而且帶文字（硬規則 3）。
 *
 * 比 Today 多一個 tone："warn" 會畫黃色左緣色條 —— 這一頁除了紅（出事了）
 * 還要表達黃（快貼線），設計稿 L1621 的階段清單就是這樣畫的。
 */
function ElRow({ tone, title, metas, tag, onClick }) {
  return (
    <RowButton
      tone={tone === "attention" || tone === "warn" ? tone : ""}
      onClick={onClick}
      title={title}
      meta={metas.length > 0 ? metas.map((m, i) => <span key={`${i}-${m}`}>{m}</span>) : null}
      side={
        <StatusChip kind={tag.kind} mark={tag.mark}>
          {tag.text}
        </StatusChip>
      }
    />
  );
}

ElRow.propTypes = {
  tone: PropTypes.string,
  title: PropTypes.string.isRequired,
  metas: PropTypes.array,
  tag: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};
ElRow.defaultProps = { tone: "", metas: [], onClick: () => {} };

/* ===================== 畫面 ===================== */

function ScheduleGoals(props) {
  const navigate = useNavigate();
  const { toast, confirmDelete } = useApp();
  const { GET_ProjectList, GET_PhaseList, GET_ScheduleWorkItems, GET_HolidayList } = props;

  /*
   * 誰看得到哪一顆按鈕。
   * schedule.create ＝ 訂里程碑（PM／高階主管），schedule.edit ＝ 排工項（RD 主管），
   * 沿用 pages/Project/Detail.js 既有的兩個權限碼，不要再發明第三個。
   * 這裡只決定「畫不畫」；真正的把關在後端的 [PermissionOnly]。
   */
  const canCreateGoal = useMemo(() => hasPermission("schedule.create"), []);
  const canPlan = useMemo(() => hasPermission("schedule.edit"), []);

  /*
   * 黃燈的門檻（「設定 → 偏好設定」的里程碑提醒門檻）。
   * 讀不到就退回預設值，此時 loaded 是 false —— 畫面上要講的是「預設值」而不是
   * 「設定好的值」，兩者差在使用者能不能去那一頁驗證它。
   */
  const { bufferDays, loaded: bufferLoaded, loading: bufferLoading } = useGoalBufferDays();

  /*
   * 選到的專案記在網址上，不只記在 state。
   *
   * 進去改一個里程碑再返回時，這一頁會重新掛載、state 歸零，然後掉回
   * 「清單的第一個專案」—— 每改一次就要重選一次。網址帶著 projectId 的話，
   * 返回按鈕、重新整理、把連結貼給別人，看到的都會是同一個專案。
   * 相鄰的三頁（新增里程碑／排工項／這週誰要交什麼）本來就是用 linkTo 帶這個參數，
   * 這裡只是把回程補上。
   */
  const [params, setParams] = useSearchParams();
  const [projSel, setProjSelState] = useState(() => params.get("projectId") || "");

  const setProjSel = useCallback(
    (v) => {
      setProjSelState(v);
      /* replace 不是 push：換專案是「看別的東西」，不該在上一頁堆出一長串歷史 */
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v) next.set("projectId", String(v));
          else next.delete("projectId");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  /*
   * 三段內容改成分頁。
   *
   * 三段疊在同一頁的實際長度是：14 張里程碑卡 + 14 個階段區塊 + 22 筆未排工項，
   * 捲軸拉到底要滾很久，而且中段那 14 個階段大多是空的，等於一路捲過去都在看
   * 「這個里程碑底下還沒有工項」。分頁之後每一段都在第一個畫面裡看得完。
   *
   * 用 MUI 的 Tabs（審核頁也是這一套），不是自己做一組切換器。
   */
  const [tab, setTab] = useState("goals");
  /* 「資料回來了沒」與「資料是空的」要分開：兩者都是 0 筆，但一個該顯示載入中、
     一個該顯示空狀態。混在一起的話每次進頁面都會先閃一下「還沒有里程碑」。 */
  const [phasesReady, setPhasesReady] = useState(false);
  const [itemsReady, setItemsReady] = useState(false);

  /* 國定假日：reducer 只留最後一次查詢的那一年，所以在頁面裡自己累積成一張表 */
  const [holidayMap, setHolidayMap] = useState({});
  const askedYears = useRef([]);

  const [yearTick, setYearTick] = useState(0);

  useEffect(() => {
    GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
  }, [GET_ProjectList]);

  const projects = useMemo(() => {
    const arr = Array.isArray(props.projectList)
      ? props.projectList
      : _.get(props.projectList, "data_list", []);
    return (Array.isArray(arr) ? arr : []).filter(Boolean).map((p) => normalizeProject(p));
  }, [props.projectList]);

  /*
   * 預設看第一個專案。
   * /Phase/List/{projectId} 是單一專案的端點，沒有「全部專案」的投影，所以
   * 設計稿 L1415 的「全部專案（主管總覽）」這個選項不放出來 ——
   * 放了就得在前端對 N 個專案各打一次，或者選了之後給一片空白。
   */
  useEffect(() => {
    if (!projSel && projects.length) setProjSel(String(projects[0].id));
  }, [projects, projSel, setProjSel]);

  useEffect(() => {
    if (!projSel) return;
    setPhasesReady(false);
    setItemsReady(false);
    /* loading 參數在 saga 的 finally 呼叫，成功或失敗都會走到，不會卡在載入中 */
    GET_PhaseList(Number(projSel), null, (v) => {
      if (!v) setPhasesReady(true);
    });
    /*
     * take 要寫出來。
     *
     * /Schedule/WorkItems 的預設是 200 筆（ScheduleController，上限 500），而排序是
     * ProjectId → StartDate → DueDate —— 被截掉的正好是起始日最晚的那一批，
     * 也就是最可能決定 lastEndYmd（工項做完預計哪一天）的那幾件。
     * 不帶 take 的話，超過 200 件工項的專案在這一頁會看不到最晚那幾件、算出綠燈，
     * 而排程讀的是全部工項、算出紅燈寄信 —— 同一個里程碑畫面與信完全相反。
     * 500 是後端夾住的上限，再大也沒有用；真的超過 500 件的專案要改成分頁把整份撈完。
     */
    GET_ScheduleWorkItems({ projectId: Number(projSel), take: 500 }, null, (v) => {
      if (!v) setItemsReady(true);
    });
  }, [projSel, GET_PhaseList, GET_ScheduleWorkItems]);

  /* ---------- 正規化 ---------- */

  const goalsRaw = useMemo(() => {
    const arr = Array.isArray(props.phaseList)
      ? props.phaseList
      : _.get(props.phaseList, "data_list", []);
    return (Array.isArray(arr) ? arr : [])
      .filter(Boolean)
      .filter((p) => String(_.get(p, "project_id", projSel)) === String(projSel))
      .map((p) => ({
        id: _.get(p, "id"),
        name: String(_.get(p, "name") || "未命名的里程碑"),
        /*
          「要達成」是用完整編輯器打的，存進來是 HTML。這張卡是兩行的預覽，
          直接印字串會把 <div>、<p> 當文字顯示（畫面上真的看得到「要達成：<div>完成人員…」）。
          用 toPlainText 去標籤而不是 RichView：卡片只有兩行的高度，
          塞一個會自己排版的區塊進來，-webkit-line-clamp 夾不住。
        */
        desc: toPlainText(_.get(p, "description")),
        endYmd: ymdOf(_.get(p, "end_date")),
        startYmd: ymdOf(_.get(p, "start_date")),
        createdYmd: ymdOf(_.get(p, "create_at")),
        /*
          訂定者與確認人。/Phase/List 現在兩組都回：create_id / create_name 與
          confirmer / confirmer_name，姓名換不到時是空字串。
          這兩個欄位只是讓卡片把設計稿那兩行寫完整，不參與任何黃／紅判定 ——
          rdSummary.js 把整個 p 展開帶到 goals 上，所以這裡加完就跟著過去了。
        */
        createdBy: personLabel(_.get(p, "create_name"), _.get(p, "create_id")),
        confirmer: String(_.get(p, "confirmer") || ""),
        confirmerName: personLabel(_.get(p, "confirmer_name"), _.get(p, "confirmer")),
        statusCode: Number(_.get(p, "status") || 0),
        sortOrder: Number(_.get(p, "sort_order") || 0),
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [props.phaseList, projSel]);

  /*
   * 軸上的工項走 /Schedule/WorkItems 而不是 /Schedule/Timeline。
   * Timeline 的形狀（專案底下巢狀 tasks[] 與 phases[]）看起來最像時程軸，
   * 但它的 tasks 投影沒有 phase_id（見 ScheduleService.GetTimeline），
   * 也就無法把工項掛到里程碑底下，更無法算出「哪幾件沒排進任何里程碑」。
   * WorkItems 兩個欄位都有，是唯一撐得住這一頁的那一支。
   */
  const tasksRaw = useMemo(() => {
    const arr = Array.isArray(props.workItems)
      ? props.workItems
      : _.get(props.workItems, "data_list", []);
    return (Array.isArray(arr) ? arr : [])
      .filter(Boolean)
      /* 換專案時重抓還沒回來，先在前端擋掉上一個專案殘留的工項，
         免得切換瞬間閃出別的專案的長條 */
      .filter((t) => String(_.get(t, "project_id")) === String(projSel))
      .map((t) => ({
        id: _.get(t, "id"),
        title: String(_.get(t, "title") || "未命名工項"),
        assignee: String(_.get(t, "assignee_name") || ""),
        startYmd: ymdOf(_.get(t, "start_date")),
        dueYmd: ymdOf(_.get(t, "due_date")),
        statusKey: statusKey(_.get(t, "status")),
        phaseId: _.get(t, "phase_id"),
      }));
  }, [props.workItems, projSel]);

  /*
   * 今天的日界。用字串當依賴而不是 dayjs 物件：dayjs() 每次 render 都是新物件，
   * 放進依賴陣列等於每次都重算；日期字串只有跨午夜才會變。
   */
  const todayYmd = dayjs().format("YYYY-MM-DD");

  const view = useMemo(
    () =>
      buildScheduleView({
        phases: goalsRaw,
        tasks: tasksRaw,
        todayYmd,
        bufferDays,
        holidayMap,
      }),
    [goalsRaw, tasksRaw, todayYmd, bufferDays, holidayMap]
  );

  /*
   * 假日一年查一次，查回來再看還缺哪一年（GET_HolidayList 是 takeLatest，
   * 兩年一起送的話前一次會被取消）。查不到也沒關係 —— 上班日至少會扣掉週末。
   */
  useEffect(() => {
    if (!view.rangeStart || !view.rangeEnd) return;
    const years = [];
    for (let y = Number(view.rangeStart.slice(0, 4)); y <= Number(view.rangeEnd.slice(0, 4)); y += 1) {
      years.push(y);
    }
    const next = years.find((y) => !askedYears.current.includes(y));
    if (next == null) return;
    askedYears.current.push(next);
    GET_HolidayList({ year: next }, () => setYearTick((t) => t + 1), null);
  }, [view.rangeStart, view.rangeEnd, yearTick, GET_HolidayList]);

  useEffect(() => {
    const list = Array.isArray(props.holidayList)
      ? props.holidayList
      : _.get(props.holidayList, "data_list", []);
    if (!Array.isArray(list) || !list.length) return;
    setHolidayMap((prev) => {
      let changed = false;
      const next = { ...prev };
      list.forEach((h) => {
        const ymd = ymdOf(_.get(h, "date", _.get(h, "Date", "")));
        if (!ymd) return;
        const name = _.get(h, "name", _.get(h, "Name", "")) || "國定假日";
        if (next[ymd] !== name) {
          next[ymd] = name;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [props.holidayList]);

  /* ---------- 導頁 ---------- */

  /* 相鄰的三頁（排工項／新增里程碑／這週誰要交什麼）都要知道現在看的是哪個專案，
     不帶過去的話使用者一進去又要再選一次 */
  const linkTo = (path, phaseId) => {
    const params = new URLSearchParams();
    if (projSel) params.set("projectId", String(projSel));
    if (phaseId != null) params.set("phaseId", String(phaseId));
    const s = params.toString();
    return s ? `${path}?${s}` : path;
  };
  /*
   * 刪除里程碑。
   *
   * 端點一直都在（DELETE /Phase/{phaseId}，掛 schedule.edit），saga 與 service
   * 前端也早就寫好了，只是這一頁從來沒有把入口畫出來 —— 訂錯一個里程碑就只能
   * 改日期改名字，刪不掉，結果時程軸上永遠掛著一條沒有人要的線。
   *
   * 底下還有工項時後端會擋（PhaseService.DeletePhase 會丟例外），
   * 但這裡先自己擋一次並說清楚原因：等按下去才看到一行紅字，使用者已經
   * 先在「確定要刪除嗎」上按過一次「確定」了。
   *
   * 權限用 canPlan（schedule.edit）而不是 canCreateGoal（schedule.create）——
   * 後端 PhaseController 的 DeletePhase 掛的就是 schedule.edit，
   * 這裡跟著它，才不會畫一顆按下去必定 403 的鈕。
   */
  const deleteGoal = (g) => {
    if (g.counts.total > 0) {
      toast(
        `「${g.name}」尚有 ${g.counts.total} 件工項，請先將工項改排至其他里程碑或刪除後，再刪除此里程碑`,
        "error"
      );
      return;
    }
    confirmDelete({
      title: "確定刪除里程碑？",
      subtitle: "此操作無法復原",
      body: `確定要刪除「${g.name}」嗎？此里程碑目前沒有工項。`,
      onConfirm: () =>
        props.DELETE_Phase(
          g.id,
          () => {
            toast("里程碑已刪除");
            /* 重抓里程碑清單：刪掉之後軸、階段清單、風險橫幅都要跟著重算 */
            GET_PhaseList(Number(projSel), null, null);
          },
          null
        ),
    });
  };

  const goNewGoal = () => navigate(linkTo("/Schedule/Goal/New"));
  const goPlan = (phaseId) => navigate(linkTo("/Schedule/Goal/Plan", phaseId));
  const goEditGoal = (phaseId) => navigate(linkTo("/Schedule/Goal/New", phaseId));

  const proj = projects.find((p) => String(p.id) === String(projSel));
  const ready = phasesReady && itemsReady;
  const { attention, warn } = view.alerts;

  /*
   * 兩條橫幅的句子用字串拼好再交給 JSX。
   *
   * 直接把中文寫成多行 JSX 文字節點的話，Babel 會在換行處補一個半形空格，
   * 中文句子中間就會多出一個看得見的空隙。粗體引言與後半句之間的全形空格
   * 照設計稿 L1424 原樣留在字串裡，它的作用是隔開粗體的結論與後半句的說明。
   *
   * 設計稿的句子裡還有「林總訂的里程碑」。訂定者現在回得來了（create_name），
   * 但這兩條橫幅講的是「接下來要找誰處理」—— 答案是 RD 主管，不是訂里程碑的人。
   * 把訂定者塞進來只會把唯一一句結論拉長，所以那個子句仍然略過；
   * 誰訂的、誰要確認，底下每一張里程碑卡上都寫著。
   */
  const attentionLead = attention
    ? `${longDate(attention.goal.endYmd)}「${attention.goal.name}」時程不足　`
    : "";
  const attentionBody = (() => {
    if (!attention) return "";
    const tail = attention.others > 0 ? `另有 ${attention.others} 個里程碑時程不足。` : "";
    if (attention.kind === "overrun" && attention.task) {
      return `「${attention.task.title}」目前排定至 ${longDate(attention.task.dueYmd)}，超出 ${attention.days} 天。請聯絡 RD 主管重新安排或增派人力。${tail}`;
    }
    const left = attention.goal.counts.total - attention.goal.counts.done;
    return `已超過里程碑日期 ${attention.days} 天，尚有 ${left} 件未完成。請聯絡 RD 主管重新安排，或將里程碑調整為可達成的日期。${tail}`;
  })();

  const warnLead = warn
    ? `${longDate(warn.goal.endYmd)}「${warn.goal.name}」緩衝不足　`
    : "";
  const warnBody = (() => {
    if (!warn) return "";
    /* 日曆天與上班天不一樣時兩個都寫出來：「只剩 2 天」如果那兩天是週末，
       實際上一天都沒有，而這正是最會出事的情形 */
    const wd =
      warn.workdays !== null && warn.workdays !== warn.days
        ? `（扣除週末與國定假日僅 ${warn.workdays} 個工作日）`
        : "";
    const tail = warn.others > 0 ? `另有 ${warn.others} 個里程碑緩衝不足。` : "";
    /* 讀不到設定值時用的是退路，不能講得像有人設定過 —— 使用者去偏好設定頁一看就會對不上。
       「還在讀」也不能講成「讀不到」（見 useGoalBufferDays 的 loading） */
    const src = bufferLoaded
      ? "，可於「設定 → 偏好設定」變更"
      : bufferLoading
        ? "（設定值讀取中，暫用預設值）"
        : "（無法讀取設定值，暫用預設值）";
    return `工項預計 ${longDate(warn.goal.lastEndYmd)} 完成，緩衝僅剩 ${warn.days} 天${wd}。提醒門檻為 ${view.bufferDays} 天${src}。${tail}`;
  })();

  /* 每件工項在軸上的顏色與那一句話。階段清單要用同一份，兩種畫法才不會各說各話 */
  const stateById = useMemo(() => {
    const m = new Map();
    view.bars.forEach((b) => m.set(String(b.id), b));
    return m;
  }, [view.bars]);

  /* ---------- 片段 ---------- */

  /**
   * 一個里程碑一列。
   *
   * 原本是 .el-goalcards 的卡片牆（設計稿 L1433-1460）。設計稿那張圖只有三個里程碑，
   * 卡片牆看起來剛好；真資料一個專案七到三十個，每張卡四到六行、最小寬度 280px，
   * 結果是要捲過好幾個畫面才看得完，而這一頁要回答的問題只有
   * 「哪一天要交什麼、哪幾個有風險」。
   *
   * 改成列表。長相跟「新增里程碑」頁的既有里程碑清單一致（貼齊卡緣的一列一列）——
   * 同一個東西在兩個畫面本來就該長一樣，而那份清單已經是這個專案驗過的列樣式。
   * 「要達成」的全文收進 title，列上只留一行，需要細節的人點「修改」進去看。
   */
  const goalRow = (g) => {
    const tag = goalTag(g);
    /* 次要資訊併成一行，各自缺就各自不出現（硬規則 2）：
       「還沒決定確認人」是常態不是錯誤，不要為了湊格式寫「確認人：—」 */
    const sub = [
      g.createdBy ? `建立者：${g.createdBy}` : "",
      g.createdYmd ? `建立於 ${longDate(g.createdYmd)}` : "",
      g.confirmerName ? `確認人：${g.confirmerName}` : "",
      countsLine(g),
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <DataRow
        key={g.id}
        /* 左緣的顏色條是這一列唯一的燈號來源（卡片版是整張卡描邊） */
        tone={LEVEL_COLOR[g.level] ? g.level : ""}
        side={
          <>
            <StatusChip kind={tag.kind} mark={tag.mark} sx={{ flex: "0 0 auto" }}>
              {tag.text}
            </StatusChip>

            {/* 三顆都不是 contained：整頁唯一那顆在標題列（硬規則 1） */}
            <Button variant="text" onClick={() => goPlan(g.id)}>
              檢視工項
            </Button>
            {canCreateGoal ? (
              <Button variant="text" onClick={() => goEditGoal(g.id)}>
                編輯
              </Button>
            ) : null}
            {canPlan ? (
              <Button variant="text" onClick={() => deleteGoal(g)}>
                刪除
              </Button>
            ) : null}
          </>
        }
      >
        <Box sx={{ minWidth: 0, flex: "1 1 22em" }}>
          <Typography sx={{ fontWeight: 600 }}>
            {g.endYmd ? longDate(g.endYmd) : "未設定里程碑日期"}
            {" · "}
            {g.name}
          </Typography>
          {/* 「要達成」只留一行，全文在 title；這一頁要看的是哪天交什麼，不是規格內容 */}
          {g.desc ? (
            <Typography noWrap title={g.desc}>
              {g.desc}
            </Typography>
          ) : null}
          {sub ? (
            <Typography variant="body2" color="text.secondary">
              {sub}
            </Typography>
          ) : null}
        </Box>
      </DataRow>
    );
  };

  const trackGoals = view.goals.filter((g) => g.ratio !== null);
  const trackReadable = trackGoals.length > 0;

  const stageRow = (t) => {
    const st = stateById.get(String(t.id));
    /* 階段清單的徽章沿用軸上的那一句：同一件工項在兩種畫法裡必須講一樣的話。
       這是 Today 那套 tagOf 的延伸（期限一樣搶下 attention），多的是
       「超過里程碑日期」與「只剩 N 天緩衝」—— 這一頁本來就是在講里程碑。 */
    const tag = st
      ? { kind: LEVEL_TAG[st.level] || LEVEL_TAG.plan, mark: st.mark, text: st.text }
      : tagOf(t, todayYmd);
    const tone = st && (st.level === "attention" || st.level === "warn") ? st.level : "";
    return (
      <ElRow
        key={t.id}
        tone={tone}
        title={t.title}
        metas={[t.assignee || "未指派", rangeText(t, todayYmd)]}
        tag={tag}
        onClick={() => {
          if (t.id != null) navigate(`/Task/${t.id}`);
        }}
      />
    );
  };

  return (
    <>
      {/* ---------- 標題列（設計稿 L1403-1409） ---------- */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="h1" component="h1">
          時程
        </Typography>
        <Box sx={{ flex: "1 1 auto" }} />
        {/*
          角色差異用「顯示／不顯示」與文案表達，不用灰掉的按鈕：
          PM／高階主管的主要動作是訂里程碑，RD 主管的主要動作是排工項。
          整頁仍然只有一顆 contained —— 有 schedule.create 時是「新增里程碑」，
          沒有但有 schedule.edit 時才輪到「排工項」，兩個都沒有就一顆都不畫。
        */}
        {canCreateGoal ? (
          <Button variant="contained" onClick={goNewGoal}>
            新增里程碑
          </Button>
        ) : null}
        {canPlan ? (
          <Button variant={canCreateGoal ? "outlined" : "contained"} onClick={() => goPlan(null)}>
            工項排程
          </Button>
        ) : null}
      </Stack>

      {/* ---------- 篩選列（設計稿 L1411-1420） ---------- */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
        <Typography component="label" variant="body2" color="text.secondary" htmlFor="sch-proj">
          專案：
        </Typography>
        <TextField
          select
          id="sch-proj"
          sx={{ width: "auto" }}
          slotProps={{ select: { native: true } }}
          value={projSel}
          onChange={(e) => setProjSel(e.target.value)}
        >
          {projects.length === 0 ? <option value="">載入中…</option> : null}
          {projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </TextField>
        {/* 「畫法：里程碑時程軸」拿掉了：那顆按了不會有任何事（你本來就在這個畫法上），
           一個只有一個選項、而且選不動的切換器，只是在佔位置。真的會跳頁的
           「這週誰要交什麼」留著。 */}
        <Button variant="text" onClick={() => navigate(linkTo("/Schedule/Week"))}>
          本週到期項目
        </Button>
      </Stack>

      {/* ----------
        先說結論的兩條風險橫幅（設計稿 L1422-1431）—— 這是整頁的主體。
        只有真的算出該狀態才渲染：沒事也掛一條「目前沒有風險」的橫幅，
        三天之後就沒有人會看它了，真的有事那天也一樣不會看（硬規則 2、4）。
      ---------- */}
      {ready && attention ? (
        <Banner kind="attention" icon={<MdWarningAmber />} role="alert" sx={{ mt: 2 }}>
          <strong>{attentionLead}</strong>
          {attentionBody}
        </Banner>
      ) : null}

      {ready && warn ? (
        <Banner kind="warn" icon={<MdErrorOutline />} sx={{ mt: 1.5 }}>
          <strong>{warnLead}</strong>
          {warnBody}
        </Banner>
      ) : null}

      {/* ---------- 里程碑卡（設計稿 L1466-1492） ---------- */}
      <Typography variant="h3" component="h2" sx={{ mt: 4 }}>
        {proj ? `${proj.name} 的里程碑` : "里程碑"}
      </Typography>
      {ready ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {`${view.counts.goals} 個里程碑 · ${view.counts.planned} 件工項已排入、${view.counts.unplanned} 件未排入`}
        </Typography>
      ) : null}

      {/* 風險橫幅留在分頁之外：那是整頁的結論，切到哪一頁都要看得到 */}
      {ready && view.goals.length > 0 ? (
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="時程檢視"
          sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}
        >
          {[
            ["goals", `里程碑 ${view.counts.goals}`],
            ["plan", "已排定工項"],
            ["unplanned", `未排入里程碑 ${view.counts.unplanned}`],
          ].map(([key, label]) => (
            <Tab key={key} value={key} label={label} />
          ))}
        </Tabs>
      ) : null}

      {!ready ? (
        <Card sx={{ mt: 2 }}>
          <SkeletonRows rows={4} label="里程碑載入中" />
        </Card>
      ) : view.goals.length === 0 ? (
        /* 空的區塊不要畫出來，整塊換成空狀態（硬規則 2）：
           沒有里程碑時，軸、階段清單、風險橫幅通通沒有意義，一律不渲染 */
        <EmptyState
          sx={{ mt: 2 }}
          title="本專案尚無里程碑"
          text={"里程碑用於設定「何時交付什麼」。建立後，此處將顯示各里程碑的剩餘天數，以及所屬工項是否排得下；排不下時會明確提示。"}
          action={
            canCreateGoal ? (
              <Button variant="text" startIcon={<MdAdd aria-hidden="true" />} onClick={goNewGoal}>
                新增里程碑
              </Button>
            ) : null
          }
        />
      ) : tab === "goals" ? (
        <>
          {/* 完成的定義講在清單正上方：里程碑沒有「手動標成完成」這回事（後端 PutPhase 不收狀態），
              不講清楚的話，看到一排「未開始 · 尚無工項」會以為是系統漏算 */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {GOAL_DONE_RULE}
          </Typography>
          <Card sx={{ mt: 1 }}>{view.goals.map(goalRow)}</Card>
        </>
      ) : null}

      {/* ---------- 工項排在哪一段：橫向軸 ＋ 直向階段清單 ---------- */}
      {ready && tab === "plan" && view.goals.length > 0 ? (
        <Card component="section" sx={{ mt: 3, p: 3 }}>
          <Typography variant="h3" component="h2">
            已排定工項
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {longDate(view.rangeStart)} – {longDate(view.rangeEnd)}
          </Typography>

          {/* ----
            橫向軸（設計稿 L1494-1596）。

            設計稿那張圖假設的是「一個專案、3 個里程碑、8 週」，把整段時間壓進螢幕寬。
            真資料跨好幾年、十幾個里程碑，壓進螢幕寬之後刻度與旗標會疊成一團。
            所以每週給足寬度、外層橫向捲動（捲軸藏起來），同一段裡位置太近的旗標往下分層，
            保證不重疊。
          ---- */}
          {/*
            橫向時程軸（components 在 GoalTimeline.js）：每個里程碑一個區塊、長條照日期精確畫、
            標出今天與里程碑日期，讀法與操作寫在圖的正上方。大字模式與窄螢幕改看下方的直向階段清單。
          */}
          {trackReadable ? (
            <Box sx={TRACK_SX}>
              <GoalTimeline
                view={view}
                todayYmd={todayYmd}
                legend={LEGEND}
                bufferNote={
                  bufferLoaded
                    ? `「緩衝不足」的門檻為 ${view.bufferDays} 天，可於「設定 → 偏好設定」變更。`
                    : bufferLoading
                      ? `「緩衝不足」的門檻為 ${view.bufferDays} 天（設定值讀取中，暫用預設值）。`
                      : `「緩衝不足」的門檻為 ${view.bufferDays} 天（無法讀取設定值，暫用預設值）。`
                }
                onOpenTask={(id) => navigate(`/Task/${id}`)}
                onOpenGoal={(id) => goPlan(id)}
              />
            </Box>
          ) : null}

          {/* ----
            直向階段清單（設計稿 L1599-1690）：同一份資料的第二種畫法。
            大字模式與 ≤900px 時把橫向軸隱藏、這一段顯示（見 STAGES_SX），
            所以這一段一定要在 DOM 裡，否則大字模式與手機整片空白。
            不畫橫向軸的時候（trackReadable 為 false）這一段一律顯示 ——
            這是設計稿沒有的情況（它假設軸一定畫得出來）。
          ---- */}
          <Box sx={{ mt: 3, ...(trackReadable ? STAGES_SX : { display: "block" }) }}>
            {view.goals
              .filter((g) => g.tasks.length > 0)
              .map((g) => {
                const tag = goalTag(g);
                return (
                  <Box component="section" key={`stage-${g.id}`} sx={{ "& + &": { mt: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "baseline",
                        rowGap: 1,
                        columnGap: 2,
                        px: 3,
                        py: 2,
                        bgcolor: "var(--surface-sunken)",
                        borderRadius: "var(--radius-md) var(--radius-md) 0 0",
                      }}
                    >
                      <Typography component="span" variant="h3">
                        {g.endYmd ? longDate(g.endYmd) : "未設定里程碑日期"}
                      </Typography>
                      <Typography component="span">{g.name}</Typography>
                      {/* 誰訂的（設計稿 L1605）。換不到姓名就整格不渲染 */}
                      {g.createdBy ? (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
                        >
                          <MdPersonOutline aria-hidden="true" />
                          {`建立者：${g.createdBy}`}
                        </Typography>
                      ) : null}
                      <Box component="span" sx={{ flex: "1 1 auto" }} />
                      <StatusChip kind={tag.kind} mark={tag.mark}>
                        {tag.text}
                      </StatusChip>
                    </Box>
                    <Box
                      sx={{
                        border: 1,
                        borderTop: 0,
                        borderColor: "divider",
                        borderRadius: "0 0 var(--radius-md) var(--radius-md)",
                        overflow: "hidden",
                        bgcolor: "background.paper",
                      }}
                    >
                      {g.overrunDays > 0 && g.worstTask ? (
                        /* 貼在最上面的橫幅要切齊上緣，所以不要圓角 */
                        <Banner kind="attention" icon={<MdWarningAmber />} sx={{ borderRadius: 0 }}>
                          {`「${g.worstTask.title}」排定至 ${longDate(g.worstTask.dueYmd)}，將超出里程碑日期 ${g.overrunDays} 天。`}
                        </Banner>
                      ) : null}
                      {g.tasks.map(stageRow)}
                    </Box>
                  </Box>
                );
              })}
          </Box>

          {/*
            底下還沒有工項的里程碑併成一行。

            原本是每個里程碑都畫一塊「這個里程碑底下還沒有工項」的空狀態 ——
            實測 14 個里程碑裡 14 個都是空的，整頁就變成一路捲過去都在看同一句話，
            佔掉一千多 px 卻沒有任何資訊量。設計稿規則 2 本來就寫「空的區塊不要
            畫出來，只留『＋ 加入…』的文字入口」，這裡就是那個入口。
          */}
          {(() => {
            const empty = view.goals.filter((g) => g.tasks.length === 0);
            if (empty.length === 0) return null;
            return (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {`另有 ${empty.length} 個里程碑尚無工項：${empty
                    .map((g) => g.name)
                    .join("、")}。沒有工項時無法評估是否來得及，這些里程碑不會出現在上方的風險提醒中。`}
                </Typography>
                {canPlan ? (
                  <Button
                    variant="text"
                    startIcon={<MdAdd aria-hidden="true" />}
                    sx={{ mt: 1 }}
                    onClick={() => goPlan(null)}
                  >
                    將工項排入里程碑
                  </Button>
                ) : null}
              </Box>
            );
          })()}
        </Card>
      ) : null}

      {/* ---------- 沒排進任何里程碑的工項（設計稿 L1693-1705） ---------- */}
      {ready && tab === "unplanned" && view.unplanned.length > 0 ? (
        <Card component="section" sx={{ mt: 3, p: 3 }}>
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="h3" component="h2">
              未排入里程碑的工項
            </Typography>
            <StatusChip kind="closed" mark={<MdRemove />}>
              {view.unplanned.length} 件
            </StatusChip>
          </Stack>
          <Typography sx={{ mt: 1 }}>
            {`這 ${view.unplanned.length} 件未對應任何里程碑，不會顯示於時程軸，也不會計入上方的風險提醒。`}
          </Typography>
          {/* 清單貼齊外層卡片左右緣，上方一條分隔線跟說明隔開（不另包一層卡片） */}
          <Box sx={{ mt: 2, mx: -3, borderTop: 1, borderColor: "divider" }}>
            {view.unplanned.slice(0, UNPLANNED_MAX).map((t) => {
              /* tone 由徽章決定，不另外算一次：這一頁上半部的 stageRow 也是
                 「徽章紅、列就紅」，少傳 tone 的話同樣逾期的工項會變成上半部有紅邊條、
                 這一區只有一顆紅徽章配素邊條。
                 這裡不會有 warn（黃）—— 那一級講的是「只剩幾天緩衝」，
                 而沒排進任何里程碑的工項本來就算不出緩衝。 */
              const tag = tagOf(t, todayYmd);
              return (
                <ElRow
                  key={t.id}
                  tone={tag.kind === "attention" ? "attention" : ""}
                  title={t.title}
                  metas={[t.assignee || "未指派", rangeText(t, todayYmd)]}
                  tag={tag}
                  onClick={() => {
                    if (t.id != null) navigate(`/Task/${t.id}`);
                  }}
                />
              );
            })}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button variant="text" onClick={() => goPlan(null)}>
              {view.unplanned.length > UNPLANNED_MAX
                ? `將全部 ${view.unplanned.length} 件排入里程碑`
                : "將這些工項排入里程碑"}
            </Button>
          </Box>
        </Card>
      ) : null}
    </>
  );
}

/*
 * 資料來源
 *   · GET_ProjectList          → 專案下拉
 *   · GET_PhaseList(projectId) → state.Phase.List，里程碑本身
 *   · GET_ScheduleWorkItems    → state.Schedule.WorkItems，軸上與階段清單的工項
 *   · GET_HolidayList(year)    → state.Holiday.List，緩衝的上班日換算
 *   · useGoalBufferDays        → GET /Setting/Goal，黃燈的門檻天數
 *
 * 誠實的缺口（畫面上一律不編造，缺哪個就整行不渲染）：
 *   1. 訂定者與確認人換不到姓名時只寫得出帳號 —— 後端是拿帳號去 Admin 表換姓名，
 *      帳號被刪掉就換不回來（見 personLabel）。這時候寫帳號，不編名字。
 *   2. 確認人只是一個欄位，系統不會因此通知他，也沒有「他確認了」這個動作可以按。
 *      畫面上不寫成「已送交確認」之類的話。
 *
 * （原本列在這裡的三個缺口都補上了：
 *   · 提醒緩衝天數 —— 存在 system_setting 的 goal.buffer_days，在「設定 → 偏好設定」改。
 *     讀不到時才退回 rdSummary.js 的 DEFAULT_BUFFER_DAYS，而且畫面上會把「這是預設值」講出來。
 *   · 訂定者 —— /Phase/List 補了 create_id / create_name。
 *   · 確認人 —— project_phase 補了 confirmer 欄位，投影回 confirmer / confirmer_name。）
 */
const mapStateToProps = (state) => ({
  projectList: _.get(state, "Project.List", []),
  phaseList: _.get(state, "Phase.List", []),
  workItems: _.get(state, "Schedule.WorkItems", []),
  holidayList: _.get(state, "Holiday.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
  GET_PhaseList: (projectId, callback, loading) =>
    dispatch({ type: "GET_PhaseList", projectId, callback, loading }),
  GET_ScheduleWorkItems: (payload, callback, loading) =>
    dispatch({ type: "GET_ScheduleWorkItems", payload, callback, loading }),
  GET_HolidayList: (payload, callback, loading) =>
    dispatch({ type: "GET_HolidayList", payload, callback, loading }),
  DELETE_Phase: (phaseId, callback, loading) =>
    dispatch({ type: "DELETE_Phase", phaseId, callback, loading }),
});

ScheduleGoals.propTypes = {
  projectList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  phaseList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  workItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  holidayList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_ProjectList: PropTypes.func,
  GET_PhaseList: PropTypes.func,
  GET_ScheduleWorkItems: PropTypes.func,
  GET_HolidayList: PropTypes.func,
  DELETE_Phase: PropTypes.func,
};

ScheduleGoals.defaultProps = {
  projectList: [],
  phaseList: [],
  workItems: [],
  holidayList: [],
  GET_ProjectList: () => {},
  GET_PhaseList: () => {},
  GET_ScheduleWorkItems: () => {},
  GET_HolidayList: () => {},
  DELETE_Phase: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleGoals);
